import { DatePipe } from '@angular/common';
import { EventEmitter, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RealisationHourtype } from '../classes/realisation';
import { HourtypePipe } from './hourtype.pipe';
import { IsNumericPipe } from './is-numeric.pipe';
import { MinutesPipe } from './minutes.pipe';
import { ShortNumberPipe } from './short-number.pipe';

@Pipe({
    name: 'aiDynamicCellContent',
    standalone: false,
})
export class AiDynamicCellContentPipe implements PipeTransform {
    private readonly employeeKeywords = [
        'medewerker',
        'persoon',
        'gebruiker',
        'materieel',
        'naam',
        'projectleider',
        'uitvoerder',
    ];
    private readonly projectNumberRegex = /\b(\d{4}-\d{3,4})\b/g;

    constructor(private sanitizer: DomSanitizer) {}

    transform(
        value: string,
        values: { afas_employee_id?: string },
        fieldName: string,
        eventEmitter?: EventEmitter<string>
    ): SafeHtml | string {
        if (!value) {
            return value;
        }

        // Handle date formats
        if (this.isDateFormat(value)) {
            return new DatePipe('nl-NL').transform(value, 'EEEE d MMM yyyy') || value;
        }
        if (this.isDateTimeFormat(value)) {
            return new DatePipe('nl-NL').transform(value, 'EEEE d MMM yyyy HH:mm') || value;
        }

        // Handle numeric values
        if (new IsNumericPipe().transform(value)) {
            const numValue = parseFloat(value);
            return this.isHoursField(fieldName)
                ? new MinutesPipe().transform(numValue * 60)
                : new ShortNumberPipe().transform(numValue);
        }

        // Handle employee fields
        if (this.isEmployeeField(fieldName) && values.afas_employee_id && eventEmitter) {
            return this.createClickableSpan(values, value, 'Klik om te zoeken naar deze medewerker', eventEmitter);
        }

        // Handle RealisationHourtype
        if (Object.values(RealisationHourtype).includes(value as RealisationHourtype)) {
            return new HourtypePipe().transform(value as RealisationHourtype);
        }

        // Handle project numbers
        return this.createProjectNumberLinks(value, eventEmitter);
    }

    private createClickableSpan(
        values: { afas_employee_id?: string },
        displayValue: string,
        title: string,
        eventEmitter: EventEmitter<string>
    ): SafeHtml {
        const uniqueId = 'clickable-' + Math.random().toString(36).substr(2, 9);
        let emitValue = values.afas_employee_id;

        // Check for date values in the values object and append them to emitValue
        Object.values(values)
            .filter(value => value?.length > 0)
            .forEach(val => {
                if (this.isDateFormat(val) || this.isDateTimeFormat(val)) {
                    const formatted = new DatePipe('nl-NL').transform(val, 'd MMM yyyy');
                    if (formatted) {
                        emitValue += ' uren ' + formatted;
                    }
                }
            });

        this.addClickListener(uniqueId, emitValue, eventEmitter);
        return this.sanitizer.bypassSecurityTrustHtml(
            `<span id="${uniqueId}" class="project-number" title="${title}">${displayValue}</span>`
        );
    }

    private addClickListener(uniqueId: string, emitValue: string, eventEmitter: EventEmitter<string>): void {
        setTimeout(() => {
            const element = document.getElementById(uniqueId);
            if (element) {
                element.addEventListener('click', () => eventEmitter.emit(emitValue));
            }
        });
    }

    private isDateFormat(value: string): boolean {
        return /^\d{4}-\d{2}-\d{2}$/.test(String(value));
    }

    private isDateTimeFormat(value: string): boolean {
        return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(String(value));
    }

    private isHoursField(fieldName: string): boolean {
        return /hour|hours|uur|uren/i.test(fieldName);
    }

    private isEmployeeField(fieldName: string): boolean {
        return this.employeeKeywords.some(keyword => fieldName.toLowerCase().includes(keyword));
    }

    private createProjectNumberLinks(value: string, eventEmitter?: EventEmitter<string>): SafeHtml {
        // Find all project numbers using the specified regex pattern
        const projectNumberRegex = /\b(\d{4}-\d{1,4})\b/g;

        // Replace each match with a clickable span
        const transformedValue = value.replace(projectNumberRegex, match => {
            const uniqueId = 'project-' + Math.random().toString(36).substr(2, 9);
            if (eventEmitter) {
                this.addClickListener(uniqueId, match, eventEmitter);
            }
            return `<span id="${uniqueId}" class="project-number">${match}</span>`;
        });

        return this.sanitizer.bypassSecurityTrustHtml(transformedValue);
    }
}
